import { createContext, useContext, useEffect, useMemo, useState, ReactNode } from "react";
import ApiService from "../../services/api-service";
import { showErrorToast, showSuccessToast } from "../../lib/toast";

interface ThemeContextValue {
  theme: string;
  setTheme: (value: string) => void;
}

const ThemeContext = createContext<ThemeContextValue>({
  theme: "system",
  setTheme: () => {},
});

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error("useTheme must be used within a ThemeProvider");
  }
  return context;
};

export function ThemeProvider({ children }: { children: ReactNode }) {
  const [theme, setTheme] = useState(() => {
    if (typeof window !== "undefined") {
      return localStorage.getItem("inspirelens-theme") || "system";
    }
    return "system";
  });

  const updateTheme = async (newTheme: string) => {
    const user = JSON.parse(localStorage.getItem("inspirelens_user") || "null");
    const userId = user?.id;

    if (!userId) {
      setTheme(newTheme);
      return;
    }

    try {
      setTheme(newTheme);
      const response = await ApiService.updateTheme(newTheme, userId);
      user.mode = newTheme;
      localStorage.setItem("inspirelens_user", JSON.stringify(user));
      showSuccessToast("Theme updated successfully!");
    } catch (error: any) {
      console.error("Failed to update theme:", error);
      showErrorToast("Theme updated locally, but failed to sync with server");
    }
  };

  useEffect(() => {
    const root = window.document.documentElement;
    root.classList.remove("light", "dark");

    if (theme === "system") {
      const systemTheme = window.matchMedia("(prefers-color-scheme: dark)").matches ? "dark" : "light";
      root.classList.add(systemTheme);
    } else {
      root.classList.add(theme);
    }

    localStorage.setItem("inspirelens-theme", theme);
  }, [theme]);

  useEffect(() => {
    if (theme === "system") {
      const mediaQuery = window.matchMedia("(prefers-color-scheme: dark)");
      const handleChange = () => {
        const root = window.document.documentElement;
        root.classList.remove("light", "dark");
        root.classList.add(mediaQuery.matches ? "dark" : "light");
      };

      mediaQuery.addEventListener("change", handleChange);
      return () => mediaQuery.removeEventListener("change", handleChange);
    }
  }, [theme]);

  const value = useMemo(
    () => ({
      theme,
      setTheme: updateTheme,
    }),
    [theme]
  );

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}
